import { Box, Dialog, DialogActions, DialogContent, Typography, useTheme } from '@mui/material';
import { FiAlertTriangle } from 'react-icons/fi';
import Button from 'components/Common/Button';

type Props = {
  open: boolean;
  phoneNumberLabel: string;
  businessName?: string | null;
  assignedToAgents: boolean;
  isPending?: boolean;
  onCancel: () => void;
  onConfirm: () => void;
};

export default function ReleaseNumberConfirmDialog({
  open,
  phoneNumberLabel,
  businessName,
  assignedToAgents,
  isPending = false,
  onCancel,
  onConfirm
}: Props) {
  const theme = useTheme();
  const businessLabel = businessName?.trim() || 'your business';
  const assignmentLine = assignedToAgents
    ? ' It is currently assigned to AI Agents.'
    : ' It is not currently assigned to any AI Agent.';

  return (
    <Dialog
      open={open}
      onClose={isPending ? undefined : onCancel}
      maxWidth={false}
      PaperProps={{
        sx: {
          borderRadius: '6px',
          width: 500,
          maxWidth: 'calc(100% - 32px)',
          boxShadow: '0 20px 40px rgba(15, 23, 42, 0.16)'
        }
      }}
    >
      <DialogContent sx={{ pt: 3.5, pb: 2, px: 3 }}>
        <Box display='flex' flexDirection='column' alignItems='center' textAlign='center'>
          <Box
            sx={{
              width: 64,
              height: 64,
              borderRadius: '50%',
              bgcolor: theme.palette.error.light,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mb: 2
            }}
          >
            <FiAlertTriangle size={30} color={theme.palette.error.main} />
          </Box>

          <Typography fontWeight={550} fontSize={20} color='#111827' mb={1.25} lineHeight={1.3}>
            Release Phone Number
          </Typography>

          <Typography fontSize={13} color='#6B7280' lineHeight={1.55} mb={2.25}>
            Are you sure you want to release &apos;{phoneNumberLabel}&apos; from {businessLabel}?{assignmentLine}
          </Typography>
        </Box>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            bgcolor: theme.palette.error.light,
            borderRadius: '4px',
            px: 1.5,
            py: 1.15
          }}
        >
          <Box sx={{ color: theme.palette.error.main, display: 'inline-flex', flexShrink: 0 }}>
            <FiAlertTriangle size={16} />
          </Box>
          <Typography fontSize={13} color={theme.palette.error.main} lineHeight={1.4}>
            Released from your carrier. This can&apos;t be undone.
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, pt: 0.5, gap: 1.25, justifyContent: 'center' }}>
        <Button
          variant='outlined'
          color='inherit'
          onClick={onCancel}
          disabled={isPending}
          sx={{
            textTransform: 'none',
            borderRadius: '999px',
            fontWeight: 500,
            borderColor: '#E5E7EB',
            color: '#111827',
            bgcolor: '#fff',
            px: 2.5,
            minWidth: 100,
            boxShadow: 'none',
            '&:hover': { borderColor: '#E5E7EB', bgcolor: '#F9FAFB' }
          }}
        >
          Cancel
        </Button>
        <Button
          variant='contained'
          onClick={onConfirm}
          isPending={isPending}
          sx={{
            textTransform: 'none',
            borderRadius: '999px',
            fontWeight: 500,
            bgcolor: theme.palette.error.main,
            boxShadow: 'none',
            px: 2.5,
            minWidth: 160,
            '&:hover': { bgcolor: theme.palette.error.dark, boxShadow: 'none' }
          }}
        >
          Yes, Release Number
        </Button>
      </DialogActions>
    </Dialog>
  );
}
